import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { InputAdornment } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import clsx from 'clsx';
import { isValid, parseISO } from 'date-fns';
import React, { FC } from 'react';
import { toISO8601LocalString } from 'utils/helpers';

// Accepts a Date, a local ISO string (parseISO) or a Date.toString() string (new Date fallback).
const toDate = (v: Date | string | null | undefined): Date | undefined => {
  if (!v) return undefined;
  if (typeof v !== 'string') return v;
  const p = parseISO(v);
  return isValid(p) ? p : new Date(v);
};

const DateIcon = () => <FontAwesomeIcon icon={faCalendarAlt} size="xs" />;

const useStyles = makeStyles((theme: any) => ({
  input: {
    '&:focus': { backgroundColor: theme.palette.background.default },
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: theme.palette.background.default,
    },
  },
  naked: {
    '&:focus': { backgroundColor: theme.palette.background.default },
    '& .MuiFilledInput-input': {
      paddingTop: 10,
      paddingLeft: 0,
    },
    '& .MuiFilledInput-root': {
      textTransform: 'none',
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      backgroundColor: alpha(theme.palette.background.default, 0),
    },
  },
  icon: { padding: 0, marginRight: 0 },
}));

type DatePickerProps = {
  id?: string;
  name?: string;
  label?: string;
  disabled?: boolean;
  disableToolbar?: boolean;
  format?: string;
  color?: 'primary' | 'secondary' | undefined;
  inputVariant?: 'filled' | 'standard' | 'outlined' | undefined;
  naked?: boolean;
  minDate?: Date | string | null;
  maxDate?: Date | string | null;
  value: Date | string | null;
  error?: boolean | undefined;
  helperText?: React.ReactNode;
  placeholder?: string;
  handleBlur?: any;
  onChange: (date: string | null | undefined) => void;
  InputLabelProps?: any
  InputProps?: any;
  inputRef?: any;
  replaceTimeWithCurrentBrowserTime?: boolean,
  handleFocus?: any,
};

const DatePicker: FC<DatePickerProps> = ({
  id,
  name,
  label,
  disabled = false,
  disableToolbar = true,
  format = 'MM/dd/yyyy',
  color = 'primary',
  inputVariant = 'filled',
  naked = false,
  minDate,
  maxDate,
  value,
  error = false,
  helperText,
  placeholder = '',
  handleBlur,
  onChange,
  InputLabelProps,
  InputProps,
  inputRef,
  replaceTimeWithCurrentBrowserTime = true,
  handleFocus,
}) => {
  const classes = useStyles();

  const onDateChange = (date: Date | null) => {
    onChange(toISO8601LocalString(date, replaceTimeWithCurrentBrowserTime));
  };

  const toPickerDate = (v: Date | string | null | undefined) => {
    if (v === null || v === undefined || v === '') return null;
    return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? parseISO(v) : new Date(v);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiDatePicker
        disabled={disabled}
        label={label}
        className={clsx({ [classes.input]: !naked, [classes.naked]: naked })}
        format={format}
        minDate={minDate ? toPickerDate(minDate)! : undefined}
        maxDate={maxDate ? toPickerDate(maxDate)! : undefined}
        value={toPickerDate(value)}
        onChange={onDateChange}
        inputRef={inputRef}
        slots={{
          openPickerIcon: () => (
            <InputAdornment position="end">
              <FontAwesomeIcon icon={faCalendarAlt} size="xs" />
            </InputAdornment>
          ),
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            ...(id ? { id } : {}),
            name,
            placeholder,
            color,
            variant: inputVariant,
            error: error || undefined,
            helperText,
            onBlur: handleBlur,
            onFocus: handleFocus,
            InputLabelProps,
            InputProps: { ...InputProps, disableUnderline: naked },
          },
          openPickerButton: { className: classes.icon },
          toolbar: { hidden: disableToolbar },
        }}
      />
    </LocalizationProvider>
  );
};

export default DatePicker;
